<template>
    <hox-modal @close="close">
        <template #header>
            {{ title }}
        </template>
        <div class="account-rule-modal__wrap">
            <Form
                ref="addAccountForm"
                class="account-rule__form"
                :rules="validationRules"
                :model="formData"
                @submit.prevent="onFormSubmit"
            >
                <FormItem ref="name" label="Rule name" prop="name">
                    <Input v-model="formData.name" auto-complete="off" data-testid="add-platform-account__name" />
                </FormItem>

                <FormItem ref="adSetId" label="Adset Id" prop="adSetId">
                    <Input
                        v-model="formData.adSetId"
                        auto-complete="off"
                        data-testid="add-platform-account__adset-id"
                    />
                </FormItem>

                <FormItem ref="status" label="Status" prop="status">
                    <Select v-model="formData.status" data-testid="add-platform-account__status">
                        <Option key="draft" data-testid="add-platform-account__status-draft" value="draft"
                            >Draft</Option
                        >
                        <Option key="active" data-testid="add-platform-account__status-active" value="active"
                            >Active</Option
                        >
                    </Select>
                </FormItem>

                <Button type="primary" data-testid="new-platform-account__modal-button" @click="onFormSubmit">
                    {{ buttonLabel }}
                </Button>
            </Form>
        </div>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { checkEmptySpaces, checkOnlyNumbers } from "@/utils";
import type { PlatformAccount, Rule } from "@/Connect/types";
import { useConnectStore } from "@/Connect/store";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

interface RuleFormData {
    name: string;
    adSetId: string;
    status: "draft" | "active";
}

export default defineComponent({
    name: "AccountRuleFormModal",
    props: {
        platformAccount: {
            type: Object as PropType<PlatformAccount>,
            required: true
        },
        rule: {
            type: Object as PropType<Rule>
        }
    },

    emits: ["close"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            formData: {
                name: this.rule?.name || "",
                adSetId: this.rule?.rulePlatformDetails.adSetId || "",
                status: this.rule?.status || "draft"
            } as RuleFormData,

            validationRules: {
                name: [
                    {
                        required: true,
                        message: "Please enter an rule name",
                        trigger: "blur"
                    },
                    {
                        message: "Rule name must not start with space",
                        validator: (rule, value) => checkEmptySpaces(value),
                        trigger: "blur"
                    }
                ],
                adSetId: [
                    {
                        required: true,
                        message: "Please enter an Adset ID.",
                        trigger: "blur"
                    },
                    {
                        message: "Adset Id must contain only numbers",
                        validator: (rule, value) => checkOnlyNumbers(value),
                        trigger: "blur"
                    }
                ],
                status: [
                    {
                        required: true,
                        message: "Please select the status",
                        trigger: "blur"
                    }
                ]
            },
            isCompleteEventTracked: false
        };
    },

    computed: {
        ...mapStores(useConnectStore),

        title() {
            if (this.rule) {
                return "Edit rule";
            }

            return "Add new rule";
        },

        buttonLabel() {
            if (this.rule) {
                return "Update";
            }

            return "Add";
        }
    },

    mounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.CONNECT,
            featureContext: this.title
        });
    },

    methods: {
        close() {
            if (!this.isCompleteEventTracked) {
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.CONNECT,
                    featureContext: `Close ${this.title}`
                });
            }
            this.$emit("close");
        },

        onFormSubmit() {
            this.$refs.addAccountForm.validate((valid: boolean) => {
                if (!valid) {
                    this.$snackbar.error("Please correctly fill in the required fields");

                    return;
                }

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.CONNECT,
                    featureContext: this.title
                });
                this.isCompleteEventTracked = true;

                this.$emit("close");

                if (this.rule) {
                    this.$snackbar.info("Account rule updated");

                    this.connectStore.updateRule(this.platformAccount.name, this.rule.name, this.formData);

                    return;
                }

                this.$snackbar.info("Account rule added");

                this.connectStore.addRule(this.platformAccount.name, this.formData);
            });
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.account-rule {
    &__form {
        width: 100%;
        padding-right: 0;
        .ivu-FormItem {
            margin-bottom: 10px;
            &.ivu-FormItem-error {
                margin-bottom: 20px;
            }
        }
        .ivu-FormItem-label {
            font-weight: bold;
        }
        .ivu-btn-primary {
            margin-top: 30px;
        }
        .ivu-icon {
            font-size: 12px;
        }
    }
    &__wrap {
        display: flex;
    }

    &__info {
        padding-left: 15px;
        display: flex;
        align-items: center;
    }
}
</style>
